import * as React from 'react';
import type { Column, ColumnDef, ColumnFiltersState, FilterFn, Table } from '@tanstack/react-table';
import { ListFilter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils/cn';

export type ColumnFilterKind = 'number' | 'percent' | 'duration';
export type CompareOp = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'between';

export type NumericFilterValue = {
  op: CompareOp;
  value: string;
  valueTo?: string;
  kind: ColumnFilterKind;
};

const OPS: { value: CompareOp; label: string; symbol: string }[] = [
  { value: 'gt', label: 'Greater than', symbol: '>' },
  { value: 'gte', label: 'At least', symbol: '≥' },
  { value: 'lt', label: 'Less than', symbol: '<' },
  { value: 'lte', label: 'At most', symbol: '≤' },
  { value: 'eq', label: 'Equals', symbol: '=' },
  { value: 'between', label: 'Between', symbol: '–' },
];

export function isNumericFilterActive(value: unknown): value is NumericFilterValue {
  if (!value || typeof value !== 'object') return false;
  const v = value as NumericFilterValue;
  if (!v.op) return false;
  const a = Number(v.value);
  if (!Number.isFinite(a)) return false;
  if (v.op === 'between') return Number.isFinite(Number(v.valueTo));
  return true;
}

function compare(n: number, op: CompareOp, a: number, b?: number) {
  switch (op) {
    case 'gt':
      return n > a;
    case 'gte':
      return n >= a;
    case 'lt':
      return n < a;
    case 'lte':
      return n <= a;
    case 'eq':
      return n === a;
    case 'between':
      return n >= Math.min(a, b ?? a) && n <= Math.max(a, b ?? a);
    default:
      return true;
  }
}

/** Client-side compare for count / percent / duration columns. */
export const numericCompareFilter: FilterFn<unknown> = (row, columnId, filterValue) => {
  if (!isNumericFilterActive(filterValue)) return true;
  const raw = Number(row.getValue(columnId));
  const n = Number.isFinite(raw) ? raw : 0;
  const a = Number(filterValue.value);
  const b = Number(filterValue.valueTo);
  const scale = filterValue.kind === 'duration' ? 60 : 1;
  return compare(n, filterValue.op, a * scale, Number.isFinite(b) ? b * scale : undefined);
};

function unitHint(kind: ColumnFilterKind) {
  if (kind === 'percent') return '%';
  if (kind === 'duration') return 'min';
  return '';
}

function formatChip(header: string, filter: NumericFilterValue) {
  const op = OPS.find((o) => o.value === filter.op);
  const unit = unitHint(filter.kind);
  const suffix = unit ? ` ${unit}` : '';
  if (filter.op === 'between') {
    return `${header} ${filter.value}–${filter.valueTo}${suffix}`;
  }
  return `${header} ${op?.symbol ?? ''} ${filter.value}${suffix}`;
}

function headerLabel(column: Column<any, unknown>) {
  const h = column.columnDef.header;
  if (typeof h === 'string' && h.trim()) return h;
  return String(column.id || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getColumnFilterKind(column: Column<any, unknown>): ColumnFilterKind | null {
  const kind = (column.columnDef.meta as { filter?: ColumnFilterKind } | undefined)?.filter;
  return kind === 'number' || kind === 'percent' || kind === 'duration' ? kind : null;
}

export function DataTableColumnFilter({
  column,
  kind,
}: {
  column: Column<any, unknown>;
  kind: ColumnFilterKind;
}) {
  const current = (column.getFilterValue() as NumericFilterValue | undefined) ?? null;
  const active = isNumericFilterActive(current);
  const [open, setOpen] = React.useState(false);
  const [op, setOp] = React.useState<CompareOp>(current?.op ?? 'gt');
  const [value, setValue] = React.useState(current?.value ?? '');
  const [valueTo, setValueTo] = React.useState(current?.valueTo ?? '');

  React.useEffect(() => {
    if (!open) return;
    setOp(current?.op ?? 'gt');
    setValue(current?.value ?? '');
    setValueTo(current?.valueTo ?? '');
  }, [open, current?.op, current?.value, current?.valueTo]);

  const apply = (nextOp: CompareOp, nextValue: string, nextValueTo: string) => {
    const draft: NumericFilterValue = { op: nextOp, value: nextValue, valueTo: nextValueTo, kind };
    if (isNumericFilterActive(draft)) column.setFilterValue(draft);
    else column.setFilterValue(undefined);
  };

  const hint = unitHint(kind);
  const placeholder = kind === 'percent' ? 'e.g. 50' : kind === 'duration' ? 'e.g. 5' : 'e.g. 20';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Filter ${headerLabel(column)}`}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className={cn(
            'inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
            active ? 'text-primary' : 'opacity-40 hover:opacity-100 group-hover:opacity-100',
          )}
        >
          <ListFilter className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-[1100] w-56 p-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="mb-2 text-xs font-semibold text-foreground">{headerLabel(column)}</div>
        <select
          value={op}
          onChange={(e) => {
            const next = e.target.value as CompareOp;
            setOp(next);
            apply(next, value, valueTo);
          }}
          className="input-embossed mb-2 h-8 w-full rounded-md px-2 text-xs"
        >
          {OPS.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <div className={cn('flex items-center gap-1.5', op === 'between' && 'flex-col items-stretch')}>
          <div className="relative flex-1">
            <Input
              type="number"
              min={0}
              step={kind === 'percent' || kind === 'duration' ? '0.1' : '1'}
              value={value}
              autoFocus
              onChange={(e) => {
                const next = e.target.value;
                setValue(next);
                apply(op, next, valueTo);
              }}
              placeholder={placeholder}
              className={cn('h-8 text-xs', hint && 'pr-9')}
            />
            {hint ? (
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
                {hint}
              </span>
            ) : null}
          </div>
          {op === 'between' ? (
            <div className="relative flex-1">
              <Input
                type="number"
                min={0}
                step={kind === 'percent' || kind === 'duration' ? '0.1' : '1'}
                value={valueTo}
                onChange={(e) => {
                  const next = e.target.value;
                  setValueTo(next);
                  apply(op, value, next);
                }}
                placeholder="and"
                className={cn('h-8 text-xs', hint && 'pr-9')}
              />
              {hint ? (
                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
                  {hint}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        {active ? (
          <button
            type="button"
            className="mt-2 text-[11px] text-muted-foreground hover:text-foreground"
            onClick={() => {
              column.setFilterValue(undefined);
              setValue('');
              setValueTo('');
              setOpen(false);
            }}
          >
            Clear
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export function DataTableFilterChips<TData>({ table }: { table: Table<TData> }) {
  const chips = table.getState().columnFilters.flatMap((filter) => {
    const column = table.getColumn(filter.id);
    if (!column || !isNumericFilterActive(filter.value)) return [];
    return [
      {
        id: filter.id,
        label: formatChip(headerLabel(column), filter.value),
      },
    ];
  });

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary/10 px-3 py-2">
      <span className="text-xs font-semibold text-foreground">Active filters</span>
      {chips.map((chip) => (
        <span
          key={chip.id}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground"
        >
          {chip.label}
          <button
            type="button"
            aria-label={`Remove ${chip.label}`}
            title="Remove filter"
            className="inline-flex h-5 w-5 items-center justify-center rounded-sm bg-primary-foreground/20 hover:bg-primary-foreground/35"
            onClick={() => table.getColumn(chip.id)?.setFilterValue(undefined)}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      ))}
      {chips.length > 1 ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs font-medium"
          onClick={() => table.resetColumnFilters()}
        >
          Clear all
        </Button>
      ) : null}
    </div>
  );
}

export function attachNumericColumnFilters<TData, TValue>(
  columns: ColumnDef<TData, TValue>[],
): ColumnDef<TData, TValue>[] {
  return columns.map((col) => {
    const kind = (col.meta as { filter?: ColumnFilterKind } | undefined)?.filter;
    if (kind !== 'number' && kind !== 'percent' && kind !== 'duration') {
      return { ...col, enableColumnFilter: col.enableColumnFilter ?? false };
    }
    return {
      ...col,
      enableColumnFilter: true,
      filterFn: numericCompareFilter as FilterFn<TData>,
    };
  });
}

export function activeNumericFilterCount(filters: ColumnFiltersState) {
  return filters.filter((f) => isNumericFilterActive(f.value)).length;
}
